const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

export const roundCurrencyAmount = (value) => Math.round(toNumber(value, 0) * 100) / 100;

export const calculatePartialPaymentAmounts = (totalAmount, percentage) => {
  const total = roundCurrencyAmount(totalAmount);
  const pct = roundCurrencyAmount(percentage);
  if (!Number.isFinite(total) || total < 0) {
    throw new Error('Total amount must be a non-negative number');
  }
  if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
    throw new Error('Partial payment percentage must be between 1 and 100');
  }
  let amountPaidNow = roundCurrencyAmount((total * pct) / 100);
  if (total > 0 && amountPaidNow <= 0) {
    amountPaidNow = Math.min(total, 0.01);
  }
  if (amountPaidNow > total) {
    amountPaidNow = total;
  }
  return {
    totalAmount: total,
    partialPercentage: pct,
    amountPaidNow: roundCurrencyAmount(amountPaidNow),
    remainingAmount: roundCurrencyAmount(total - amountPaidNow),
  };
};

export const formatPaymentBreakdown = ({
  totalAmount,
  paymentOptionSelected = 'full',
  partialPaymentPercentage = null,
  amountPaidNow,
  remainingAmount,
  paymentStatusExtended = null,
} = {}) => {
  const total = roundCurrencyAmount(totalAmount);
  const option = String(paymentOptionSelected || 'full').toLowerCase() === 'partial' ? 'partial' : 'full';
  const pct =
    partialPaymentPercentage === undefined || partialPaymentPercentage === null || partialPaymentPercentage === ''
      ? null
      : roundCurrencyAmount(partialPaymentPercentage);

  if (option === 'partial' && pct && pct > 0 && pct <= 100) {
    const calculated = calculatePartialPaymentAmounts(total, pct);
    return {
      paymentOptionSelected: 'partial',
      partialPaymentPercentage: calculated.partialPercentage,
      totalAmount: calculated.totalAmount,
      amountPaidNow: roundCurrencyAmount(amountPaidNow ?? calculated.amountPaidNow),
      remainingAmount: roundCurrencyAmount(remainingAmount ?? calculated.remainingAmount),
      paymentStatusExtended:
        paymentStatusExtended ||
        (roundCurrencyAmount(remainingAmount ?? calculated.remainingAmount) > 0 ? 'partially_paid' : 'paid'),
    };
  }

  const normalizedPaidNow = roundCurrencyAmount(amountPaidNow ?? total);
  const normalizedRemaining = roundCurrencyAmount(
    remainingAmount ?? Math.max(roundCurrencyAmount(total - normalizedPaidNow), 0)
  );

  return {
    paymentOptionSelected: 'full',
    partialPaymentPercentage: pct,
    totalAmount: total,
    amountPaidNow: normalizedPaidNow,
    remainingAmount: normalizedRemaining,
    paymentStatusExtended: paymentStatusExtended || (normalizedRemaining > 0 ? 'partially_paid' : 'paid'),
  };
};

export const resolveQuotePaymentOptions = (quote = {}, totalAmount, currency = 'INR') => {
  const total = roundCurrencyAmount(totalAmount);
  const backendOptions = quote.payment_options || quote.paymentOptions || null;
  const partialOption = backendOptions?.partial || null;
  const partialPercentage =
    partialOption?.percentage ??
    quote.partial_payment_percentage ??
    quote.partialPaymentPercentage ??
    null;
  const full = {
    paymentOption: 'full',
    isAvailable: true,
    percentage: 100,
    payableNow: total,
    remainingLater: 0,
    currency,
  };
  if (!partialOption?.isAvailable || !partialPercentage) {
    return {
      full,
      partial: null,
      effectiveConfig: backendOptions?.effectiveConfig || null,
    };
  }
  const amounts = calculatePartialPaymentAmounts(total, partialPercentage);
  return {
    full,
    partial: {
      paymentOption: 'partial',
      isAvailable: true,
      percentage: amounts.partialPercentage,
      payableNow: amounts.amountPaidNow,
      remainingLater: amounts.remainingAmount,
      currency,
    },
    effectiveConfig: backendOptions?.effectiveConfig || null,
  };
};
